import { useState, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, BookOpen, Play, Loader2, AlertTriangle } from 'lucide-react';
import { CourseMediaItem } from '../../types';
import { detectVideoSourceType } from '../../utils/videoUrl';

// Lazy so an image-only gallery (the common public case) never loads the heavy player bundle.
const VideoPlayer = lazy(() => import('./VideoPlayer'));

interface MediaCarouselProps {
  items?: CourseMediaItem[];
  fallbackThumbnail?: string;
  title?: string;
  className?: string;
}

// An uploaded video is playable once it's `ready` (or has no recorded status, e.g. legacy data).
function isPlayableUpload(item: CourseMediaItem): boolean {
  return !!item.mediaId && (item.videoStatus === 'ready' || item.videoStatus === undefined);
}

function itemThumb(item: CourseMediaItem): string | undefined {
  return item.type === 'image' ? item.url || item.posterUrl : item.posterUrl;
}

// Fills the viewer edge-to-edge: a blurred, zoomed copy paints the letterbox/pillarbox area
// behind the fully-visible (object-contain) image, so off-ratio media (e.g. portrait phone
// screenshots) has no bare black bars while staying uncropped.
function FilledImage({ src, alt }: { src: string; alt: string }) {
  return (
    <div className="relative w-full h-full overflow-hidden">
      <img src={src} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl" />
      <img src={src} alt={alt} className="relative w-full h-full object-contain" />
    </div>
  );
}

// The large viewer for the active item.
function MediaView({ item, title }: { item: CourseMediaItem; title?: string }) {
  const { t } = useTranslation('common');

  if (item.type === 'image') {
    const src = item.url || item.posterUrl;
    return src ? (
      <FilledImage src={src} alt={item.caption || title || ''} />
    ) : (
      <div className="w-full h-full flex items-center justify-center"><BookOpen className="w-16 h-16 text-gray-400" /></div>
    );
  }

  // Uploaded video — play publicly (no per-user watermark); URL resolved by VideoPlayer.
  if (item.videoSource !== 'link' && item.mediaId) {
    if (isPlayableUpload(item)) {
      return (
        <Suspense fallback={<div className="w-full h-full bg-black" />}>
          <VideoPlayer mediaId={item.mediaId} publicVideo poster={item.posterUrl} className="w-full h-full" />
        </Suspense>
      );
    }
    // Not ready yet → show the poster with a status overlay instead of a broken player.
    return (
      <div className="relative w-full h-full bg-gray-900">
        {item.posterUrl && <img src={item.posterUrl} alt={title || ''} className="w-full h-full object-contain opacity-60" />}
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white">
          {item.videoStatus === 'failed' ? (
            <><AlertTriangle className="w-8 h-8 text-red-400" /><span className="text-sm">{t('carousel.videoFailed')}</span></>
          ) : (
            <><Loader2 className="w-8 h-8 animate-spin" /><span className="text-sm">{t('carousel.videoProcessing')}</span></>
          )}
        </div>
      </div>
    );
  }

  // Link video (YouTube / Vimeo / direct URL).
  const url = item.url || '';
  const sourceType = detectVideoSourceType(url);
  if (sourceType === 'youtube' || sourceType === 'vimeo') {
    return (
      <iframe
        src={url}
        title={title || 'video'}
        className="w-full h-full"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    );
  }
  return (
    <Suspense fallback={<div className="w-full h-full bg-black" />}>
      <VideoPlayer src={url} className="w-full h-full" />
    </Suspense>
  );
}

/**
 * App-Store-style media gallery: a large viewer plus a clickable thumbnail filmstrip.
 * Shared by the teaching editor's live preview and the public course-detail page.
 * Falls back to a single thumbnail (then a placeholder icon) when the gallery is empty.
 */
export default function MediaCarousel({ items = [], fallbackThumbnail, title, className = '' }: MediaCarouselProps) {
  const { t } = useTranslation('common');
  const [active, setActive] = useState(0);

  const valid = items.filter((it) => it && (it.url || it.mediaId || it.posterUrl));

  if (valid.length === 0) {
    return (
      <div className={`aspect-video bg-white rounded-xl overflow-hidden ${className}`}>
        {fallbackThumbnail ? (
          <FilledImage src={fallbackThumbnail} alt={title || ''} />
        ) : (
          <div className="w-full h-full flex items-center justify-center"><BookOpen className="w-20 h-20 text-gray-500" /></div>
        )}
      </div>
    );
  }

  const safeActive = Math.min(active, valid.length - 1);
  const current = valid[safeActive];
  const go = (next: number) => setActive((next + valid.length) % valid.length);

  return (
    <div className={className}>
      <div className="relative aspect-video bg-black rounded-xl overflow-hidden">
        <MediaView item={current} title={title} />
        {valid.length > 1 && (
          <>
            <button
              type="button"
              onClick={() => go(safeActive - 1)}
              aria-label={t('carousel.prev')}
              className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={() => go(safeActive + 1)}
              aria-label={t('carousel.next')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
              {valid.map((it, i) => (
                <span
                  key={it.id || i}
                  className={`w-1.5 h-1.5 rounded-full ${i === safeActive ? 'bg-white' : 'bg-white/40'}`}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {valid.length > 1 && (
        <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
          {valid.map((it, i) => {
            const thumb = itemThumb(it);
            return (
              <button
                key={it.id || i}
                type="button"
                onClick={() => setActive(i)}
                aria-label={`${t('carousel.imageAlt')} ${i + 1}`}
                className={`relative flex-shrink-0 w-24 aspect-video rounded-lg overflow-hidden border-2 transition-colors ${
                  i === safeActive ? 'border-gold' : 'border-transparent hover:border-gray-300'
                }`}
              >
                {thumb ? (
                  <img src={thumb} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gray-800" />
                )}
                {it.type === 'video' && (
                  <span className="absolute inset-0 flex items-center justify-center bg-black/25">
                    <Play className="w-5 h-5 text-white fill-white" />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
